import { Maximize2, MoveDiagonal2, X } from "lucide-react";
import { useEffect, useRef } from "react";
import type { DocumentPreview } from "../lib/documentSearch/types";
import { SourcePreviewPanel } from "./SourcePreviewPanel";

type SourcePreviewModalProps = {
  preview: DocumentPreview;
  pinned: boolean;
  onClose: () => void;
  onPinToggle: () => void;
  onOpenSource: () => void;
};

export function SourcePreviewModal({
  preview,
  pinned,
  onClose,
  onPinToggle,
  onOpenSource,
}: SourcePreviewModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) {
      return;
    }
    dialog.showModal();
    document.body.classList.add("preview-modal-open");
    return () => {
      document.body.classList.remove("preview-modal-open");
      if (dialog.open) {
        dialog.close();
      }
    };
  }, []);

  function closeDialog() {
    dialogRef.current?.close();
  }

  function resetSize() {
    const dialog = dialogRef.current;
    if (!dialog) {
      return;
    }
    dialog.style.width = "50vw";
    dialog.style.height = "75vh";
  }

  return (
    <dialog
      aria-label={`Document source preview: ${preview.title}`}
      className="preview-modal"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          const bounds = event.currentTarget.getBoundingClientRect();
          const outside =
            event.clientX < bounds.left ||
            event.clientX > bounds.right ||
            event.clientY < bounds.top ||
            event.clientY > bounds.bottom;
          if (outside) {
            closeDialog();
          }
        }
      }}
      onClose={onClose}
      ref={dialogRef}
    >
      <header className="preview-modal__toolbar">
        <div>
          <MoveDiagonal2 size={17} aria-hidden="true" />
          <span>Source preview</span>
          <small>Drag the bottom-right corner to resize</small>
        </div>
        <div className="preview-modal__toolbar-actions">
          <button
            aria-label="Reset preview to 50 percent of the screen"
            className="small-icon-button"
            onClick={resetSize}
            title="Reset to 50%"
            type="button"
          >
            <Maximize2 size={17} aria-hidden="true" />
          </button>
          <button
            aria-label="Close source preview"
            autoFocus
            className="small-icon-button"
            onClick={closeDialog}
            title="Close preview"
            type="button"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
      </header>

      <div className="preview-modal__content">
        <SourcePreviewPanel
          preview={preview}
          pinned={pinned}
          onPinToggle={onPinToggle}
          onOpenSource={onOpenSource}
        />
      </div>
      <span className="preview-modal__resize-cue" aria-hidden="true" />
    </dialog>
  );
}
